import { BrandChannel, CONTENT_FORMATS, formatsOf, Platform, PLATFORMS } from "@growth/schema";
import { Input } from "@growth/ui";

import { useMembers } from "../lib/workspace";
import { selectClass } from "./page-header";

/**
 * Where a brand posts: per platform, which formats, how many a week (the director plans to this),
 * and who owns it (they approve and post it from Today; anyone can when nobody does).
 */
export const ChannelsEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: BrandChannel[];
  onChange: (channels: BrandChannel[]) => void;
  disabled: boolean;
}) => {
  const members = useMembers();
  const approvers =
    members.data?.filter(
      (member) => member.userId && member.status === "ACTIVE" && member.role !== "VIEWER",
    ) ?? [];
  const byId = new Map(value.map((channel) => [channel.id, channel]));
  const update = (id: Platform, patch: Partial<BrandChannel> | null) => {
    const order = Object.keys(PLATFORMS) as Platform[];
    const next = new Map(byId);
    if (patch === null) next.delete(id);
    else
      next.set(id, {
        ...(next.get(id) ?? { id, formats: [formatsOf(id)[0]!], perWeek: 2, ownerId: null }),
        ...patch,
      });
    onChange(order.filter((platform) => next.has(platform)).map((platform) => next.get(platform)!));
  };

  return (
    <div className="divide-y divide-line rounded-lg border border-line">
      {(Object.keys(PLATFORMS) as Platform[]).map((platform) => {
        const channel = byId.get(platform);
        return (
          <div
            key={platform}
            className="grid gap-3 p-3 sm:grid-cols-[9rem_minmax(0,1fr)_7rem_12rem] sm:items-center"
          >
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="size-4"
                disabled={disabled}
                checked={Boolean(channel)}
                onChange={(event) => update(platform, event.target.checked ? {} : null)}
              />
              {PLATFORMS[platform].name}
            </label>
            {channel ? (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {formatsOf(platform).map((format) => (
                    <label key={format} className="flex items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        className="size-3.5"
                        disabled={disabled}
                        checked={channel.formats.includes(format)}
                        onChange={(event) => {
                          const formats = event.target.checked
                            ? [...channel.formats, format]
                            : channel.formats.filter((item) => item !== format);
                          if (formats.length) update(platform, { formats });
                        }}
                      />
                      {CONTENT_FORMATS[format].replace(`${PLATFORMS[platform].name} `, "")}
                    </label>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-xs text-muted">
                  <Input
                    type="number"
                    min={0}
                    max={21}
                    aria-label={`${PLATFORMS[platform].name} posts a week`}
                    disabled={disabled}
                    className="w-16"
                    value={channel.perWeek}
                    onChange={(event) =>
                      update(platform, {
                        perWeek: Math.max(0, Math.min(21, Number(event.target.value) || 0)),
                      })
                    }
                  />
                  a week
                </label>
                <select
                  aria-label={`${PLATFORMS[platform].name} owner`}
                  className={selectClass}
                  disabled={disabled}
                  value={channel.ownerId ?? ""}
                  onChange={(event) => update(platform, { ownerId: event.target.value || null })}
                >
                  <option value="">Anyone can post</option>
                  {approvers.map((member) => (
                    <option key={member.userId} value={member.userId!}>
                      {member.name ?? member.email}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <p className="text-xs text-muted sm:col-span-3">Not used</p>
            )}
          </div>
        );
      })}
    </div>
  );
};
